import { AnimatePresence, motion } from 'framer-motion'
import { useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/pagination'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { vacancies } from '../../../data/vacation'
import AnimatedTitle from '../../AnimatedTitle/AnimatedTitle'
import styles from './Vacancies.module.css'

const Vacancies = () => {
  const [activeIdx, setActiveIdx] = useState(0)
  const swiperRef = useRef<SwiperType | null>(null)

  const handleLabelClick = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index)
    }
  }

  return (
    <section id="vacancies" className={styles.vacanciesSection}>
      <div className={styles.left}>
        <div className={styles.vacancySwiper}>
          <div className={styles.topBorder}></div>
          <div className={styles.bottomBorder}></div>
          <div className={styles.leftBorder}></div>
          <div className={styles.rightBorder}></div>
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={false}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            onSlideChange={(swiper: SwiperType) => setActiveIdx(swiper.realIndex)}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper
            }}
            className={styles.vacancySwiper}
          >
            {vacancies.map((vac, idx) => (
              <SwiperSlide key={idx}>
                <div className={styles.vacancyCard}>
                  <img
                    src={vac.iconVacation}
                    alt={vac.title}
                    className={styles.iconVacation}
                    width={438}
                    height={472}
                    loading="lazy"
                  />
                  <AnimatedTitle
                    as="h2"
                    text={
                      typeof vac.title === 'string' ? vac.title.split(/<br\s*\/?>|\n/) : vac.title
                    }
                    className={styles.vacancyTitle}
                  />
                  <a
                    href="https://forms.gle/jKCuvLM8QgYUaLMf8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.respondBtn}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                    >
                      <path
                        d="M11.4727 17.2222V17.2222C11.4727 13.2334 14.7062 9.99995 18.6949 9.99995L20.5004 9.99995"
                        stroke="#0D0D0D"
                        strokeWidth="2"
                        strokeLinejoin="bevel"
                      />
                      <path
                        d="M11.4727 2.77783V2.77783C11.4727 6.76656 14.7062 10.0001 18.6949 10.0001L20.5004 10.0001"
                        stroke="#0D0D0D"
                        strokeWidth="2"
                        strokeLinejoin="bevel"
                      />
                      <path d="M20.5 10L0.5 10" stroke="#0D0D0D" strokeWidth="2" />
                    </svg>
                    Відгукнутись
                  </a>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className={styles.vacancyLabels}>
          {vacancies.map((v, i) => (
            <div
              key={i}
              className={styles.vacancyLabelWrap}
              onClick={() => handleLabelClick(i)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.progressBarWrap}>
                <div
                  className={`${styles.progressBar} ${
                    i === activeIdx ? styles.progressActive : ''
                  }`}
                  key={activeIdx}
                >
                  <div
                    className={`${styles.lineProgress} ${
                      i === activeIdx ? styles.lineProgressActive : ''
                    }`}
                    key={activeIdx}
                  ></div>
                </div>
              </div>
              <span className={`${styles.vacancyLabel} ${i === activeIdx ? styles.active : ''}`}>
                {v.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.right}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            className={styles.contentRight}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <h3 className={styles.rightTitle}>
              Друзі, {vacancies[activeIdx].title.toLowerCase()}!
            </h3>
            <p className={styles.rightDesc}>{vacancies[activeIdx].description}</p>
            <div className={styles.requirementsBox}>
              <span className={styles.requirementsTitle}>Чекаємо на тебе, якщо:</span>
              <ul className={styles.requirementsList}>
                {vacancies[activeIdx].requirements.map((req, idx) => (
                  <li key={idx}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.3466 5.67364C14.6253 2.57719 12.1543 0.254193 8.89333 0.129704C7.88258 0.104219 6.878 0.29427 5.94641 0.687211C5.01483 1.08015 4.1776 1.66697 3.49044 2.40863C1.26197 4.71819 -0.284742 8.05723 0.726151 11.277C1.54856 13.8967 4.15338 15.6991 6.85097 15.8638C7.09991 15.8783 7.34947 15.8787 7.59845 15.865C10.7762 15.6978 13.9458 13.7522 15.0751 10.6984C15.6502 9.08509 15.7445 7.33952 15.3466 5.67364ZM13.6781 8.24459C13.5465 10.006 12.7017 11.9968 11.1078 12.9616C9.07211 14.1937 6.6963 14.7654 4.39497 13.2999C0.407776 10.7606 2.76304 4.68676 6.08015 2.74321C10.2886 0.451229 13.9725 3.83131 13.6781 8.24459ZM11.725 4.95541C11.5142 4.82072 11.2691 4.74972 11.019 4.75093C10.7689 4.75214 10.5244 4.8255 10.315 4.96222C9.69748 5.33887 9.29245 5.97421 8.8184 6.50186C8.11001 7.29043 7.51903 7.93544 6.82195 8.73811C6.52729 9.07741 6.50942 8.98811 6.39004 8.80239C6.06161 8.29132 5.70842 7.57775 5.2375 7.19072C5.08463 7.06859 4.89574 7.00032 4.70012 6.9965C4.50449 6.99268 4.31308 7.05353 4.15557 7.1696C3.99806 7.28568 3.88325 7.45049 3.82897 7.63847C3.77469 7.82645 3.78397 8.02709 3.85535 8.20926C4.24121 9.05093 4.67732 9.86864 5.16132 10.658C5.50242 11.2626 6.03033 11.9943 6.72828 11.8901C7.35613 11.7963 8.05832 10.7919 8.48274 10.2026C9.03145 9.44059 9.62811 8.76177 10.197 8.04639C10.6117 7.52494 11.0804 6.95572 11.5374 6.47181C11.7679 6.2543 11.9373 5.98014 12.0288 5.67669C12.0575 5.53984 12.0438 5.39749 11.9895 5.26863C11.9352 5.13977 11.8429 5.0305 11.725 4.95541Z"
                        fill="white"
                      />
                    </svg>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
            <a
              href="https://forms.gle/jKCuvLM8QgYUaLMf8"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.respondBtnMobile}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
              >
                <path
                  d="M11.4727 17.2222V17.2222C11.4727 13.2334 14.7062 9.99995 18.6949 9.99995L20.5004 9.99995"
                  stroke="#0D0D0D"
                  strokeWidth="2"
                  strokeLinejoin="bevel"
                />
                <path
                  d="M11.4727 2.77783V2.77783C11.4727 6.76656 14.7062 10.0001 18.6949 10.0001L20.5004 10.0001"
                  stroke="#0D0D0D"
                  strokeWidth="2"
                  strokeLinejoin="bevel"
                />
                <path d="M20.5 10L0.5 10" stroke="#0D0D0D" strokeWidth="2" />
              </svg>
              Відгукнутись
            </a>
          </motion.div>
        </AnimatePresence>
        <img
          src="/images/journalVacation.svg"
          alt="Кавові вакансії"
          className={styles.newsImg}
          width={214}
          height={140}
          loading="lazy"
        />
      </div>
    </section>
  )
}

export default Vacancies
